<template>
  <div class="dash">
    <button
      v-if="mobileOpen"
      type="button"
      class="dash-overlay d-lg-none"
      aria-label="Close navigation"
      @click="mobileOpen = false"
    />
    <aside class="dash-side" :class="{ 'dash-side--open': mobileOpen }">
      <div class="dash-side-inner">
        <div class="dash-brand">
          <router-link to="/" class="dash-brand-link">
            <img class="dash-brand-logo" src="@/assets/images/logo-image.svg" alt="Lush Groom">
          </router-link>
          <button class="dash-side-close d-lg-none" type="button" @click="mobileOpen = false" aria-label="Close sidebar">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>

        <nav class="dash-nav">
          <div class="dash-nav-section">
            <router-link
              v-for="item in items"
              :key="item.to"
              class="dash-nav-link"
              :class="{ 'dash-nav-link--active': isActive(item) }"
              :to="item.to"
              @click="mobileOpen = false"
            >
              <span class="dash-nav-ic" aria-hidden="true"><i :class="item.icon"></i></span>
              <span class="dash-nav-txt">{{ item.label }}</span>
            </router-link>
          </div>
        </nav>

        <div class="dash-side-footer">
          <button type="button" class="dash-logout-btn" @click="logout">
            <i class="fa fa-sign-out me-2" aria-hidden="true"></i>
            Log out
          </button>
        </div>
      </div>
    </aside>

    <div class="dash-main">
      <div class="dash-top">
        <button class="dash-burger d-lg-none" type="button" @click="mobileOpen = true" aria-label="Open sidebar">
          <i class="fa fa-bars" aria-hidden="true"></i>
        </button>
        <div class="dash-top-title">
          <div class="dash-top-kicker inter-regular-family text-muted text-full-11">{{ kicker }}</div>
          <div class="dash-top-heading filson-black-family text-black">{{ title }}</div>
        </div>
      </div>

      <div class="dash-content">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { clearPartnerSession } from '@/utils/partnerProfileStorage';
import { revokePartnerTokenIfAny } from '@/utils/partnerAuthLogout';

export default {
  name: 'DashboardLayout',
  props: {
    title: { type: String, default: 'Overview' },
    kicker: { type: String, default: '' },
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      mobileOpen: false,
    };
  },
  watch: {
    mobileOpen(open) {
      // Prevent background scrolling when the sidebar is open on mobile.
      try {
        document.documentElement.style.overflow = open ? 'hidden' : '';
        document.body.style.overflow = open ? 'hidden' : '';
      } catch (_) {
        // ignore
      }
    },
  },
  beforeUnmount() {
    try {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    } catch (_) {
      // ignore
    }
  },
  methods: {
    async logout() {
      await revokePartnerTokenIfAny(this.$axios);
      clearPartnerSession();
      this.mobileOpen = false;
      this.$router.push({ path: '/login' });
    },
    isActive(item) {
      const norm = (s) => String(s || '').replace(/\/+$/, '') || '/';
      const p = norm(this.$route.path);
      const to = norm(item?.to || '');
      if (!to || to === '/') return false;
      if (p === to) return true;
      // Overview routes must not stay active on deeper dashboard pages (e.g. /appointments).
      const dashboardRoots = ['/dashboard/groomer', '/dashboard/salon'];
      if (dashboardRoots.includes(to)) return false;
      return p.startsWith(`${to}/`);
    },
  },
};
</script>

<style scoped>
.dash {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 270px 1fr;
  background: #f6f7fb;
}

.dash-overlay {
  position: fixed;
  inset: 0;
  border: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 15;
}

.dash-side-inner {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.dash-side {
  background: #ffffff;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  z-index: 20;
}

.dash-nav {
  flex: 1;
  overflow: auto;
  padding: 12px 10px 18px;
}

.dash-side-footer {
  padding: 12px 12px 18px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dash-logout-btn {
  width: 100%;
  border: 1px solid rgba(248, 114, 31, 0.45);
  background: rgba(248, 114, 31, 0.08);
  color: #f8721f;
  border-radius: 12px;
  padding: 10px 14px;
  font-size: 13px;
  font-family: intermedium, sans-serif;
}

.dash-logout-btn:hover {
  background: rgba(248, 114, 31, 0.15);
}

.dash-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.dash-brand-link {
  display: inline-flex;
  align-items: center;
}

.dash-brand-logo {
  width: 132px;
  height: auto;
}

.dash-side-close,
.dash-burger {
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  width: 40px;
  height: 40px;
  border-radius: 12px;
}

.dash-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  color: #111;
  text-decoration: none;
  margin-bottom: 6px;
}

.dash-nav-ic {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: #f1f5f9;
  color: #111;
}

.dash-nav-link--active {
  background: rgba(248, 114, 31, 0.12);
}

.dash-nav-link--active .dash-nav-ic {
  background: #f8721f;
  color: #fff;
}

.dash-nav-txt {
  font-size: 13px;
  font-family: intermedium, sans-serif;
}

.dash-main {
  padding: 18px 22px 28px;
}

.dash-top {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px 18px;
}

.dash-top-heading {
  font-size: 22px;
  letter-spacing: 0.02em;
}

.dash-content {
  padding: 0 4px;
}

@media (max-width: 991px) {
  .dash {
    grid-template-columns: 1fr;
  }
  .dash-side {
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(-105%);
    transition: transform 220ms ease;
    width: min(86vw, 320px);
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  }
  .dash-side--open {
    transform: translateX(0);
  }
  .dash-main {
    padding: 14px 12px 22px;
  }
  .dash-top {
    padding: 8px 2px 14px;
  }
  .dash-top-heading {
    font-size: 18px;
  }
}

@media (max-width: 360px) {
  .dash-brand-logo {
    width: 116px;
  }
  .dash-side-close,
  .dash-burger {
    width: 38px;
    height: 38px;
  }
}
</style>
